import { useEffect, useState } from 'react'
import { collection, query, getDocs, orderBy } from 'firebase/firestore'
import { db } from '../lib/firebase'
import { formatDate } from '../lib/utils'
import { Search, Monitor, Globe, Clock, User } from 'lucide-react'

interface UserSession {
  id: string
  uid: string
  ipAddress: string
  loginAt: Date
  browserInfo: {
    userAgent: string
    language: string
    platform: string
    screenWidth: number
    screenHeight: number
    timezone: string
  }
}

export default function UsersPage() {
  const [sessions, setSessions] = useState<UserSession[]>([])
  const [filteredSessions, setFilteredSessions] = useState<UserSession[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [selectedSession, setSelectedSession] = useState<UserSession | null>(null)

  useEffect(() => {
    fetchSessions()
  }, [])

  useEffect(() => {
    if (search) {
      const searchLower = search.toLowerCase()
      setFilteredSessions(sessions.filter(s => 
        s.uid.toLowerCase().includes(searchLower) ||
        s.ipAddress.toLowerCase().includes(searchLower) ||
        s.browserInfo.platform.toLowerCase().includes(searchLower)
      ))
    } else {
      setFilteredSessions(sessions)
    }
  }, [sessions, search])

  const fetchSessions = async () => {
    try {
      const q = query(
        collection(db, 'userSessions'),
        orderBy('loginAt', 'desc')
      )
      const snapshot = await getDocs(q)
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        loginAt: doc.data().loginAt?.toDate() || new Date(),
      })) as UserSession[]
      setSessions(data)
      setFilteredSessions(data)
    } catch (error) {
      console.error('Error fetching sessions:', error)
    } finally {
      setLoading(false)
    }
  }

  const getBrowserName = (userAgent: string): string => {
    if (userAgent.includes('Chrome')) return 'Chrome'
    if (userAgent.includes('Firefox')) return 'Firefox'
    if (userAgent.includes('Safari')) return 'Safari'
    if (userAgent.includes('Edge')) return 'Edge'
    return 'Diğer'
  }

  const getOSName = (platform: string): string => {
    if (platform.includes('Win')) return 'Windows'
    if (platform.includes('Mac')) return 'macOS'
    if (platform.includes('Linux')) return 'Linux'
    if (platform.includes('iPhone') || platform.includes('iPad')) return 'iOS'
    if (platform.includes('Android')) return 'Android'
    return platform
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Kullanıcı Oturumları</h2>
        <span className="text-sm text-gray-500">{filteredSessions.length} oturum</span>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Kullanıcı ID, IP veya platform ara..."
          className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sessions List */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Kullanıcı</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">IP Adresi</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Platform</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Tarih</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredSessions.map((session) => (
                  <tr 
                    key={session.id} 
                    className={`hover:bg-gray-50 cursor-pointer ${selectedSession?.id === session.id ? 'bg-primary/5' : ''}`}
                    onClick={() => setSelectedSession(session)}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                          <User className="w-4 h-4 text-gray-500" />
                        </div>
                        <span className="text-sm font-mono text-gray-700">
                          {session.uid.slice(0, 8)}...
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 font-mono">
                      {session.ipAddress}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-600">
                        {getOSName(session.browserInfo.platform)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {formatDate(session.loginAt)}
                    </td>
                  </tr>
                ))}
                {filteredSessions.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                      Oturum bulunamadı
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Session Detail */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          {selectedSession ? (
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900">Oturum Detayı</h3>

              <div className="space-y-4">
                <DetailItem
                  icon={User}
                  label="Kullanıcı ID"
                  value={selectedSession.uid}
                  mono
                />
                <DetailItem
                  icon={Globe}
                  label="IP Adresi"
                  value={selectedSession.ipAddress}
                  mono
                />
                <DetailItem
                  icon={Clock}
                  label="Giriş Zamanı"
                  value={formatDate(selectedSession.loginAt)}
                />
                <DetailItem
                  icon={Monitor}
                  label="Platform"
                  value={getOSName(selectedSession.browserInfo.platform)}
                />
              </div>

              <div className="pt-4 border-t border-gray-200">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Tarayıcı Bilgileri</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Tarayıcı:</span>
                    <span className="text-gray-900">{getBrowserName(selectedSession.browserInfo.userAgent)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Dil:</span>
                    <span className="text-gray-900">{selectedSession.browserInfo.language}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Ekran:</span>
                    <span className="text-gray-900">
                      {selectedSession.browserInfo.screenWidth}x{selectedSession.browserInfo.screenHeight}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Saat Dilimi:</span>
                    <span className="text-gray-900">{selectedSession.browserInfo.timezone}</span>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t border-gray-200">
                <h4 className="text-sm font-medium text-gray-700 mb-2">User Agent</h4>
                <p className="text-xs text-gray-500 bg-gray-50 p-2 rounded break-all">
                  {selectedSession.browserInfo.userAgent}
                </p>
              </div>
            </div>
          ) : (
            <div className="text-center text-gray-500 py-12">
              <User className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>Detay görmek için bir oturum seçin</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

interface DetailItemProps {
  icon: React.ElementType
  label: string
  value: string
  mono?: boolean
}

function DetailItem({ icon: Icon, label, value, mono }: DetailItemProps) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4 text-gray-500" />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-gray-500">{label}</p>
        <p className={`text-sm text-gray-900 break-all ${mono ? 'font-mono' : ''}`}>
          {value}
        </p>
      </div>
    </div>
  )
}
